import React, { useEffect, useState } from 'react';
import { X, Plus, Trash2, Loader2, Save } from 'lucide-react';
import { GalleryCategory } from '../../lib/hooks/useGalleryCategories';

interface CategoryManagerProps {
  isOpen: boolean;
  onClose: () => void;
  categories: GalleryCategory[];
  onSave: (next: GalleryCategory[]) => Promise<boolean>;
}

// Simple slug helper for new category ids (falls back to a timestamp).
const slugify = (name: string) => {
  const base = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9а-я]+/gi, '-')
    .replace(/(^-|-$)/g, '');
  return base || `cat-${Date.now()}`;
};

const CategoryManager: React.FC<CategoryManagerProps> = ({ isOpen, onClose, categories, onSave }) => {
  const [items, setItems] = useState<GalleryCategory[]>([]);
  const [newName, setNewName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setItems(categories.map((c) => ({ ...c })));
      setNewName('');
      setError('');
    }
  }, [isOpen, categories]);

  if (!isOpen) return null;

  const addCategory = () => {
    const name = newName.trim();
    if (!name) return;
    let id = slugify(name);
    if (items.some((c) => c.id === id)) id = `${id}-${Date.now()}`;
    setItems((prev) => [...prev, { id, name }]);
    setNewName('');
  };

  const rename = (id: string, name: string) => {
    setItems((prev) => prev.map((c) => (c.id === id ? { ...c, name } : c)));
  };

  const remove = (id: string) => {
    setItems((prev) => prev.filter((c) => c.id !== id));
  };

  const handleSave = async () => {
    const cleaned = items.map((c) => ({ id: c.id, name: c.name.trim() })).filter((c) => c.name);
    if (cleaned.length === 0) {
      setError('Трябва да има поне една категория');
      return;
    }
    setIsSaving(true);
    const ok = await onSave(cleaned);
    setIsSaving(false);
    if (ok) onClose();
    else setError('Грешка при запазване');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Категории на галерията</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto space-y-3">
          {items.map((cat) => (
            <div key={cat.id} className="flex items-center gap-2">
              <input
                type="text"
                value={cat.name}
                onChange={(e) => rename(cat.id, e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              />
              <button
                onClick={() => remove(cat.id)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Изтрий категорията"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}

          {items.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">Няма категории. Добавете първата.</p>
          )}

          <div className="flex items-center gap-2 pt-2 border-t">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCategory();
                }
              }}
              placeholder="Нова категория..."
              className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <button
              onClick={addCategory}
              className="inline-flex items-center px-3 py-2 bg-secondary text-white rounded-lg hover:bg-secondary-dark transition-colors"
            >
              <Plus className="w-5 h-5 mr-1" />
              Добави
            </button>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors">
            Отказ
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
          >
            {isSaving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
            Запази
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategoryManager;
